import React, { useState, useCallback, useEffect } from 'react';
import { usePrayer } from '../../contexts/PrayerContext';
import { useTracker } from '../../contexts/TrackerContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useQaza } from '../../contexts/QazaContext';
import { useTranslation } from 'react-i18next';
import { prayerNames } from '../../data/prayerData';
import {
  Check, Circle, Flame, TrendingUp, Calendar, Lock, AlertTriangle, Clock,
  BarChart2, ChevronDown, ChevronUp, BookOpen, Trophy, Target, AlertCircle,
} from 'lucide-react';
import SEO from '../common/SEO';
import MissedPrayerDialog from '../common/MissedPrayerDialog';
import QazaSection from '../tracker/QazaSection';
import MonthlyHeatmap from '../tracker/MonthlyHeatmap';
import PrayerBreakdown from '../tracker/PrayerBreakdown';
import { KaabaIcon, CrescentStarIcon, IslamicDivider, IslamicCorners } from '../common/IslamicIcons';
import { Plane } from 'lucide-react';
import { formatDuration } from '../../utils/formatDuration';
import CelebrationBanner, { useCelebration } from '../common/CelebrationBanner';
import StreakBadges from '../tracker/StreakBadges';

const MISSED_THRESHOLD_MIN = 30;
const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';
const LIKED_KEY = 'liked_verses_local';
const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/** Collapsible section wrapper */
const CollapsibleSection = ({ title, icon, isDarkMode, defaultOpen = true, badge, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'}`}>
      <button
        onClick={() => setOpen(p => !p)}
        className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
          isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{title}</span>
          {badge != null && badge > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white">{badge}</span>
          )}
        </div>
        {open
          ? <ChevronUp className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
          : <ChevronDown className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
        }
      </button>
      {open && (
        <div className="px-4 pb-4 animate-fade-up">
          {children}
        </div>
      )}
    </div>
  );
};

const TrackerPage = () => {
  const { prayerTimes, loading: prayerLoading } = usePrayer();
  const { todayPrayers, togglePrayer, batchTogglePrayers, streak, completedCount, totalPrayers, progress, currentDateKey, fetchTrackerData } = useTracker();
  const { isDarkMode, language, isRTL, weekStartDay, formatTime, travelMode } = useTheme();
  const { login, isAuthenticated } = useAuth();
  const { addQaza, isTodayQaza, isTodayQazaMadeUp, makeUpQaza, pending: qazaPending } = useQaza();
  const { t } = useTranslation();
  const [dialogPrayer, setDialogPrayer] = useState(null);
  const { showCelebration, dismissCelebration } = useCelebration(completedCount, totalPrayers, streak);
  const [alltime, setAlltime] = useState(null);
  const [savedVersesCount, setSavedVersesCount] = useState(0);

  // Load alltime stats for logged-in users
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${API_URL}/api/tracker/alltime`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setAlltime(d); })
      .catch(() => {});
  }, [isAuthenticated]);

  // Count saved verses from localStorage
  useEffect(() => {
    try {
      const ids = JSON.parse(localStorage.getItem(LIKED_KEY) || '[]');
      setSavedVersesCount(Array.isArray(ids) ? ids.length : 0);
    } catch { setSavedVersesCount(0); }
  }, []);

  const today = new Date();
  const dateStr = today.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  // Monthly completion % from localStorage
  const year = today.getFullYear();
  const monthNum = today.getMonth() + 1;
  const daysInMonth = new Date(year, monthNum, 0).getDate();
  let completedDaysCount = 0;
  for (let d = 1; d <= Math.min(today.getDate(), daysInMonth); d++) {
    const key = `${year}-${String(monthNum).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const stored = localStorage.getItem(`prayers_${key}`);
    if (stored) {
      const prayers = JSON.parse(stored);
      if (Object.values(prayers).filter(Boolean).length === 5) completedDaysCount++;
    }
  }
  const monthlyRate = Math.round((completedDaysCount / Math.min(today.getDate(), daysInMonth)) * 100);

  // Prayer state helpers
  const nowMinutes = () => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); };
  const getPrayerMinutes = useCallback((key) => {
    if (!prayerTimes?.[key]) return null;
    const [h, m] = prayerTimes[key].split(':').map(Number);
    return h * 60 + m;
  }, [prayerTimes]);
  const isPrayerTimeReached = (key) => !prayerLoading && !!prayerTimes?.[key] && nowMinutes() >= getPrayerMinutes(key);
  const isPrayerMissed = (key) => !prayerLoading && !!prayerTimes?.[key] && nowMinutes() > getPrayerMinutes(key) + MISSED_THRESHOLD_MIN;
  const getPrayerState = (key) => {
    if (todayPrayers[key]) return 'completed';
    if (isTodayQazaMadeUp(key, currentDateKey)) return 'qaza_completed';
    if (isTodayQaza(key, currentDateKey)) return 'qaza';
    if (isPrayerMissed(key)) return 'missed';
    if (isPrayerTimeReached(key)) return 'available';
    return 'upcoming';
  };
  const T_PRAYER_PAIRS = { Dhuhr: 'Asr', Maghrib: 'Isha' };
  const handlePrayerClick = (key) => {
    const state = getPrayerState(key);
    const comp = travelMode ? T_PRAYER_PAIRS[key] : null;
    if (state === 'completed') {
      if (comp) batchTogglePrayers([key, comp]); else togglePrayer(key); return;
    }
    if (state === 'missed') { setDialogPrayer({ key, formattedTime: formatTime(prayerTimes[key]) }); return; }
    if (state === 'available') {
      if (comp) batchTogglePrayers([key, comp]); else togglePrayer(key);
    }
  };
  const handlePrayedOnTime = () => {
    if (!dialogPrayer) return;
    const comp = travelMode ? T_PRAYER_PAIRS[dialogPrayer.key] : null;
    if (comp) batchTogglePrayers([dialogPrayer.key, comp]);
    else togglePrayer(dialogPrayer.key);
    setDialogPrayer(null);
  };
  const handleMissedIt = async () => {
    if (!dialogPrayer) return;
    const comp = travelMode ? T_PRAYER_PAIRS[dialogPrayer.key] : null;
    await addQaza(dialogPrayer.key, currentDateKey);
    if (comp) await addQaza(comp, currentDateKey);
    setDialogPrayer(null);
  };
  const handleMakeUp = useCallback(async (logId) => {
    await makeUpQaza(logId, fetchTrackerData);
  }, [makeUpQaza, fetchTrackerData]);

  // Weekly view
  const getWeekDays = () => {
    const days = [];
    const todayDayOfWeek = today.getDay();
    let daysFromStart = todayDayOfWeek - weekStartDay;
    if (daysFromStart < 0) daysFromStart += 7;
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() - daysFromStart + i);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const stored = localStorage.getItem(`prayers_${key}`);
      const prayers = stored ? JSON.parse(stored) : {};
      const completed = Object.values(prayers).filter(Boolean).length;
      const isToday = date.toDateString() === today.toDateString();
      days.push({ day: date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { weekday: 'short' }), date: date.getDate(), completed, isToday, allCompleted: completed === 5 });
    }
    return days;
  };

  const weekDays = getWeekDays();
  const remaining = totalPrayers - completedCount;

  const bgClass = isDarkMode
    ? 'bg-gradient-to-b from-[var(--bg-dark-via)] via-[var(--bg-dark-from)] to-[var(--bg-dark-anchor)]'
    : 'bg-gradient-to-b from-[var(--bg-light-via)] via-[var(--bg-light-from)] to-white';

  const cardClass = isDarkMode ? 'glass-card rounded-2xl' : 'bg-white shadow-sm border border-gray-100 rounded-2xl';

  // Spiritual score: weighted average (40% today + 30% streak + 30% monthly)
  const todayScore = Math.round((completedCount / totalPrayers) * 100);
  const streakScore = Math.min(streak * 10, 100);
  const spiritualScore = Math.round(todayScore * 0.4 + streakScore * 0.3 + monthlyRate * 0.3);

  return (
    <>
      <SEO title={t('tracker.prayerTracker')} description="Track your daily prayers and maintain your streak" path="/tracker" />

      <MissedPrayerDialog
        open={!!dialogPrayer} prayer={dialogPrayer?.key} prayerTime={dialogPrayer?.formattedTime}
        onPrayedOnTime={handlePrayedOnTime} onMissedIt={handleMissedIt} onClose={() => setDialogPrayer(null)}
      />

      {/* Celebration overlay */}
      {showCelebration && (
        <CelebrationBanner streak={streak} onDismiss={dismissCelebration} />
      )}

      <div className={`min-h-screen ${bgClass}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="px-4 pt-4 pb-3 animate-fade-up">
          <div className={`flex items-center justify-center gap-3 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <KaabaIcon
              size={36}
              primaryColor={isDarkMode ? 'rgba(251,191,36,0.85)' : 'var(--p-hex)'}
              goldColor={isDarkMode ? '#d4a017' : '#c8990a'}
            />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className={`text-2xl font-black tracking-tight leading-none ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                >
                {t('tracker.prayerTracker')}
              </h1>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{dateStr}</p>
            </div>
          </div>
        </div>

        <div className="px-4 space-y-4 pb-6">

          {/* ── Travel Mode Banner ────────────────────────────────────── */}
        {travelMode && (
          <div data-testid="tracker-travel-banner" className={`mx-4 mb-3 rounded-2xl px-4 py-3 flex items-center gap-3 ${
            isDarkMode ? 'bg-sky-500/10 border border-sky-500/20' : 'bg-sky-50 border border-sky-200'
          } ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? 'bg-sky-500/20' : 'bg-sky-100'
            }`}>
              <Plane className={`w-4 h-4 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
            </div>
            <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
              <p className={`text-sm font-bold ${isDarkMode ? 'text-sky-300' : 'text-sky-800'}`}>
                {t('settings.travelMode')} {language === 'ar' ? '— القصر والجمع' : '— Qasr & Jama\''}
              </p>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-sky-400/70' : 'text-sky-600/80'}`}>
                {language === 'ar'
                  ? 'الظهر والعصر يُجمعان · المغرب والعشاء يُجمعان · قصر في الرباعية'
                  : 'Dhuhr+Asr combined · Maghrib+Isha combined · 2 rak\'ahs for 4-unit prayers'}
              </p>
            </div>
          </div>
        )}

        {/* ── KPI Grid ──────────────────────────────────────────────── */}
          <div>
            <p className={`text-xs font-bold uppercase tracking-widest mb-2 px-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              {t('tracker.kpis')}
            </p>

            {/* Row 1: Primary stats */}
            <div className="grid grid-cols-2 gap-3 mb-3 card-grid">
              {/* Current Streak */}
              <div className={`rounded-2xl p-4 border ${isDarkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Flame className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>{t('tracker.streak')}</span>
                </div>
                <p className={`text-4xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                  >{streak}</p>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('tracker.daysInRow')}</p>
              </div>

              {/* Today */}
              <div className={`rounded-2xl p-4 border ${isDarkMode ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <TrendingUp className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{t('tracker.today')}</span>
                </div>
                <p className={`text-4xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                  >{completedCount}/{totalPrayers}</p>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('tracker.prayersCompleted')}</p>
              </div>
            </div>

            {/* Row 2: Secondary KPIs */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {/* Best streak */}
              <div className={`rounded-2xl p-3 border ${isDarkMode ? 'bg-white/5 border-white/8' : 'bg-white border-gray-100 shadow-sm'}`}>
                <Trophy className={`w-4 h-4 mb-1.5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
                <p className={`text-2xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                  >{alltime?.best_streak ?? streak}</p>
                <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{t('tracker.bestStreak')}</p>
              </div>

              {/* Monthly rate */}
              <div className={`rounded-2xl p-3 border ${isDarkMode ? 'bg-white/5 border-white/8' : 'bg-white border-gray-100 shadow-sm'}`}>
                <Target className={`w-4 h-4 mb-1.5 ${
                  monthlyRate >= 80 ? isDarkMode ? 'text-emerald-400' : 'text-emerald-500'
                  : monthlyRate >= 50 ? isDarkMode ? 'text-amber-400' : 'text-amber-500'
                  : isDarkMode ? 'text-gray-400' : 'text-gray-400'
                }`} />
                <p className={`text-2xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                  >{monthlyRate}%</p>
                <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{t('tracker.monthlyRate')}</p>
              </div>

              {/* Spiritual score */}
              <div className={`rounded-2xl p-3 border relative overflow-hidden ${
                isDarkMode ? 'bg-white/5 border-white/8' : 'bg-white border-gray-100 shadow-sm'
              }`}>
                <CrescentStarIcon size={16} color={isDarkMode ? '#fbbf24' : 'var(--p-hex)'} className="mb-1.5" />
                <p className={`text-2xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                  >{spiritualScore}</p>
                <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{t('tracker.spiritualScore')}</p>
              </div>
            </div>

            {/* Row 3: Saved verses + Qaza pending + Mosques visited */}
            <div className="grid grid-cols-3 gap-2">
              {/* Saved verses */}
              <div className={`rounded-2xl p-3 border ${
                savedVersesCount > 0
                  ? isDarkMode ? 'bg-rose-500/10 border-rose-500/20' : 'bg-rose-50 border-rose-200'
                  : isDarkMode ? 'bg-white/5 border-white/8' : 'bg-white border-gray-100 shadow-sm'
              }`}>
                <BookOpen className={`w-4 h-4 mb-1.5 ${savedVersesCount > 0 ? isDarkMode ? 'text-rose-400' : 'text-rose-500' : isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                <p className={`text-2xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                  >{savedVersesCount}</p>
                <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{t('tracker.savedVerses')}</p>
              </div>

              {/* Qaza pending */}
              <div className={`rounded-2xl p-3 border ${
                qazaPending.length > 0
                  ? isDarkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'
                  : isDarkMode ? 'bg-white/5 border-white/8' : 'bg-white border-gray-100 shadow-sm'
              }`}>
                <AlertCircle className={`w-4 h-4 mb-1.5 ${qazaPending.length > 0 ? isDarkMode ? 'text-amber-400' : 'text-amber-500' : isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                <p className={`text-2xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                  >{qazaPending.length}</p>
                <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{t('tracker.qazaPending')}</p>
              </div>

              {/* Total all-time prayers */}
              <div className={`rounded-2xl p-3 border ${isDarkMode ? 'bg-white/5 border-white/8' : 'bg-white border-gray-100 shadow-sm'}`}>
                <BarChart2 className={`w-4 h-4 mb-1.5 ${isDarkMode ? 'text-sky-400' : 'text-sky-500'}`} />
                <p className={`text-2xl font-black tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>
                  {alltime ? (alltime.total_prayers > 999 ? `${Math.floor(alltime.total_prayers / 1000)}k` : alltime.total_prayers) : 0}
                </p>
                <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{t('tracker.totalPrayers')}</p>
              </div>
            </div>
          </div>

          {/* ── Streak badges ─────────────────────────────────────────── */}
          {isAuthenticated && streak >= 7 && (
            <StreakBadges streak={streak} />
          )}

          {/* ── Today's Prayers (always visible) ──────────────────────── */}
          <IslamicDivider isDarkMode={isDarkMode} />
          <div className={`rounded-2xl p-4 relative ${
            isDarkMode
              ? 'bg-gradient-to-br from-[var(--card-dark-from)] to-[var(--card-dark-to)] border border-white/8'
              : 'bg-white shadow-lg border border-gray-100'
          }`}>
            <IslamicCorners isDarkMode={isDarkMode} opacity={isDarkMode ? 0.15 : 0.06} />
            <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h2 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
                >
                {t('tracker.todaysPrayers')}
              </h2>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                completedCount === totalPrayers
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}>
                {completedCount === totalPrayers ? t('tracker.allCompleted') : `${remaining} ${t('tracker.prayersRemaining')}`}
              </span>
            </div>

            {/* Progress bar */}
            <div className={`h-2.5 rounded-full overflow-hidden mb-5 ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
              <div className="h-full rounded-full"
                style={{ width: `${progress}%`, background: 'linear-gradient(to right,#10b981,#059669)', transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)' }} />
            </div>

            {/* Prayer rows */}
            <div className="space-y-2 stagger">
              {prayerNames.map((prayer, idx) => {
                const state = getPrayerState(prayer.key);
                const prayerTime = prayerTimes ? formatTime(prayerTimes[prayer.key]) : '';
                const isClickable = ['available', 'missed', 'completed'].includes(state);

                // Travel mode: Qasr badge + Jama' combined rendering
                const QASR_PRAYERS = ['Dhuhr', 'Asr', 'Isha'];
                const PRAYER_PAIRS_T = { Dhuhr: 'Asr', Maghrib: 'Isha' };
                const COMPANION_PRAYERS_T = new Set(['Asr', 'Isha']);
                const companion = travelMode ? PRAYER_PAIRS_T[prayer.key] : null;
                const isQasr = travelMode && QASR_PRAYERS.includes(prayer.key);

                // Skip companion prayers in travel mode (rendered under primary)
                if (travelMode && COMPANION_PRAYERS_T.has(prayer.key)) return null;

                const showJamaaLabel = false; // Companions are now hidden (not shown separately)

                return (
                  <React.Fragment key={prayer.key}>

                  <button
                    key={prayer.key}
                    data-testid={`tracker-prayer-${prayer.key}`}
                    data-state={state}
                    onClick={() => isClickable && handlePrayerClick(prayer.key)}
                    disabled={!isClickable}
                    className={`prayer-row w-full flex items-center justify-between p-3.5 rounded-xl prayer-row-enter ${
                      state === 'completed' || state === 'qaza_completed' ? isDarkMode ? 'bg-emerald-500/15 border border-emerald-500/25' : 'bg-emerald-50 border border-emerald-200'
                      : state === 'qaza' ? isDarkMode ? 'bg-red-500/10 border border-red-500/20 cursor-default' : 'bg-red-50 border border-red-200 cursor-default'
                      : state === 'missed' ? isDarkMode ? 'bg-amber-500/10 border border-amber-500/20 cursor-pointer' : 'bg-amber-50 border border-amber-200 cursor-pointer'
                      : state === 'available' ? isDarkMode ? 'bg-white/6 hover:bg-white/10 border border-transparent' : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                      : isDarkMode ? 'bg-white/4 border border-transparent opacity-50 cursor-not-allowed' : 'bg-gray-50 border border-transparent opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className={`flex items-center gap-3.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
                        state === 'completed' || state === 'qaza_completed' ? 'bg-emerald-500 text-white'
                        : state === 'qaza' ? isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-500'
                        : state === 'missed' ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'
                        : state === 'upcoming' ? isDarkMode ? 'bg-white/8 text-gray-600' : 'bg-gray-100 text-gray-400'
                        : isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {(state === 'completed' || state === 'qaza_completed') ? <Check className="w-5 h-5" />
                          : (state === 'qaza' || state === 'missed') ? <AlertTriangle className="w-4 h-4" />
                          : state === 'upcoming' ? <Lock className="w-4 h-4" />
                          : <Circle className="w-5 h-5" />}
                      </div>
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <p className={`font-semibold text-sm ${
                          state === 'completed' || state === 'qaza_completed' ? isDarkMode ? 'text-emerald-400' : 'text-emerald-700'
                          : state === 'qaza' ? isDarkMode ? 'text-red-400' : 'text-red-600'
                          : state === 'missed' ? isDarkMode ? 'text-amber-300' : 'text-amber-800'
                          : state === 'upcoming' ? isDarkMode ? 'text-gray-400' : 'text-gray-400'
                          : isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>{companion ? `${t(`prayers.${prayer.key}`)} + ${t(`prayers.${companion}`)}` : t(`prayers.${prayer.key}`)}</p>
                        {state === 'qaza_completed'
                          ? <span className={`flex items-center gap-1 mt-0.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Clock className={`w-3 h-3 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                              <span className={`text-xs font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{t('qaza.madeUpLabel')}</span>
                            </span>
                          : <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                              {prayerTime || (language === 'ar' ? prayer.key : prayer.arabic)}
                            </p>
                        }
                      </div>
                    </div>
                    {/* Qasr badge + status label */}
                    <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {isQasr && (
                        <span data-testid={`tracker-qasr-badge-${prayer.key}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                          isDarkMode ? 'bg-sky-500/20 text-sky-400' : 'bg-sky-100 text-sky-600'
                        }`}>{language === 'ar' ? '٢ رك.' : '2 rak.'}</span>
                      )}
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        state === 'completed' || state === 'qaza_completed' ? isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                        : state === 'qaza' ? isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                        : state === 'missed' ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                        : isDarkMode ? 'bg-white/8 text-gray-500' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {state === 'completed' || state === 'qaza_completed' ? t('tracker.completed')
                          : state === 'qaza' ? t('qaza.missedLabel')
                          : state === 'missed' ? t('qaza.missedLabel')
                          : state === 'available' ? t('tracker.tapToMark')
                          : t('tracker.notYet')}
                      </span>
                    </div>
                  </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* ── Collapsible: Weekly ────────────────────────────────────── */}
          <CollapsibleSection
            title={t('tracker.thisWeek')}
            icon={<Calendar className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />}
            isDarkMode={isDarkMode}
            defaultOpen={true}
          >
            <div className="flex justify-between pt-1">
              {weekDays.map((day, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{day.day}</span>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs transition-colors ${
                    day.allCompleted ? 'bg-emerald-500 text-white'
                    : day.completed > 0 ? isDarkMode ? 'bg-emerald-500/25 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                    : isDarkMode ? 'bg-white/8 text-gray-500' : 'bg-gray-100 text-gray-400'
                  } ${day.isToday ? 'ring-2 ring-offset-2 ring-[var(--p-hex)]' : ''}`}>
                    {day.date}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* ── Collapsible: Monthly heatmap ──────────────────────────── */}
          <CollapsibleSection
            title={t('tracker.monthlyOverview')}
            icon={<Calendar className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />}
            isDarkMode={isDarkMode}
            defaultOpen={false}
          >
            <div className="pt-1">
              <MonthlyHeatmap />
            </div>
          </CollapsibleSection>

          {/* ── Collapsible: Prayer breakdown ─────────────────────────── */}
          <CollapsibleSection
            title={t('tracker.prayerBreakdown')}
            icon={<BarChart2 className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />}
            isDarkMode={isDarkMode}
            defaultOpen={false}
          >
            <div className="pt-1">
              <PrayerBreakdown alltime={alltime} />
            </div>
          </CollapsibleSection>

          {/* ── Qaza section ──────────────────────────────────────────── */}
          <QazaSection onLogin={login} onMadeUp={handleMakeUp} />
        </div>
      </div>
    </>
  );
};

export default TrackerPage;
